import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import SafeIcon from '../components/common/SafeIcon';
import TypeBadge from '../components/pokemon/TypeBadge';
import StatBar from '../components/pokemon/StatBar';
import { pokemonData, getPokemonByName } from '../data/pokemonData';

const { FiPlus, FiX, FiBarChart3, FiTrendingUp, FiShare2 } = FiIcons;

function ComparePage() {
  const [selectedPokemon, setSelectedPokemon] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length > 0) {
      const results = pokemonData
        .filter(pokemon => 
          pokemon.displayName.toLowerCase().includes(query.toLowerCase()) ||
          pokemon.name.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const addPokemon = (pokemon) => {
    if (selectedPokemon.length < 4 && !selectedPokemon.find(p => p.id === pokemon.id)) {
      setSelectedPokemon([...selectedPokemon, pokemon]);
      setSearchQuery('');
      setSearchResults([]);
      setShowSearch(false);
    }
  };

  const removePokemon = (pokemonId) => {
    setSelectedPokemon(selectedPokemon.filter(p => p.id !== pokemonId));
  };

  const getStatWinner = (stat) => {
    if (selectedPokemon.length === 0) return null;
    
    const maxValue = Math.max(...selectedPokemon.map(p => p.baseStats[stat]));
    return selectedPokemon.find(p => p.baseStats[stat] === maxValue);
  };

  const shareComparison = () => {
    const pokemonNames = selectedPokemon.map(p => p.name).join(',');
    const shareUrl = `${window.location.origin}${window.location.pathname}?pokemon=${pokemonNames}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Pokemon Comparison',
        text: `Compare ${selectedPokemon.map(p => p.displayName).join(', ')} stats`,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Comparison URL copied to clipboard!');
    }
  };

  const breadcrumbItems = [
    { name: 'Compare Pokemon' }
  ];

  const compareSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Compare Pokemon Stats",
    "description": "Learn how to compare Pokemon statistics side-by-side using our Pokemon comparison tool.",
    "image": "https://example.com/compare-guide.jpg",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Select Pokemon",
        "text": "Search and select up to 4 Pokemon to compare"
      },
      {
        "@type": "HowToStep", 
        "name": "View Stats",
        "text": "Compare base stats, abilities, and other attributes side-by-side"
      },
      {
        "@type": "HowToStep",
        "name": "Analyze Results",
        "text": "See which Pokemon excels in each stat category"
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title="Compare Pokemon Stats - Side-by-Side Pokemon Comparison Tool"
        description="Compare up to 4 Pokemon side-by-side with detailed stat analysis. Compare base stats, abilities, types, and more with our advanced Pokemon comparison tool."
        keywords="pokemon comparison, compare pokemon stats, pokemon vs pokemon, pokemon stat comparison, side by side pokemon"
        url="/compare"
        schema={compareSchema}
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Compare Pokemon</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Compare up to 4 Pokemon side-by-side to analyze their strengths and weaknesses
          </p>
        </motion.div>

        {/* Add Pokemon Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 sm:mb-0">
              Selected Pokemon ({selectedPokemon.length}/4)
            </h2>
            
            <div className="flex items-center space-x-4">
              {selectedPokemon.length > 1 && (
                <button
                  onClick={shareComparison}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors focus:ring-2 focus:ring-green-500"
                >
                  <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  <span>Share</span>
                </button>
              )}
              
              {selectedPokemon.length < 4 && (
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  <span>Add Pokemon</span>
                </button>
              )}
            </div>
          </div>

          {/* Search Modal */}
          {showSearch && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-gray-50 rounded-lg border"
            >
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search Pokemon to add..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                    {searchResults.map((pokemon) => (
                      <button
                        key={pokemon.id}
                        onClick={() => addPokemon(pokemon)}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left focus:ring-2 focus:ring-blue-500"
                        disabled={selectedPokemon.find(p => p.id === pokemon.id)}
                      >
                        <img
                          src={pokemon.image}
                          alt={pokemon.displayName}
                          className="w-8 h-8 object-contain"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{pokemon.displayName}</div>
                          <div className="text-sm text-gray-500">#{pokemon.id.toString().padStart(3, '0')}</div>
                        </div>
                        <div className="flex space-x-1 ml-auto">
                          {pokemon.types.map(type => (
                            <TypeBadge key={type} type={type} size="sm" clickable={false} />
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Selected Pokemon Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {selectedPokemon.map((pokemon, index) => (
              <motion.div
                key={pokemon.id}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 rounded-lg p-4 relative"
              >
                <button
                  onClick={() => removePokemon(pokemon.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors focus:ring-2 focus:ring-red-500"
                  aria-label={`Remove ${pokemon.displayName}`}
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
                
                <div className="text-center">
                  <img
                    src={pokemon.image}
                    alt={pokemon.displayName}
                    className="w-16 h-16 object-contain mx-auto mb-2"
                  />
                  <h3 className="font-semibold text-gray-900 mb-1">{pokemon.displayName}</h3>
                  <p className="text-sm text-gray-500 mb-2">#{pokemon.id.toString().padStart(3, '0')}</p>
                  <div className="flex justify-center space-x-1">
                    {pokemon.types.map(type => (
                      <TypeBadge key={type} type={type} size="sm" clickable={false} />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
            
            {/* Add Pokemon Placeholders */}
            {Array.from({ length: 4 - selectedPokemon.length }).map((_, index) => (
              <motion.div
                key={`placeholder-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300 flex items-center justify-center min-h-[140px]"
              >
                <button
                  onClick={() => setShowSearch(true)}
                  className="flex flex-col items-center space-y-2 text-gray-500 hover:text-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 rounded p-2"
                >
                  <SafeIcon icon={FiPlus} className="w-8 h-8" />
                  <span className="text-sm">Add Pokemon</span>
                </button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Comparison Table */}
        {selectedPokemon.length > 1 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-lg p-6 mb-8"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiBarChart3} className="w-5 h-5 text-blue-600" />
              <span>Stat Comparison</span>
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-2 font-semibold text-gray-900">Stat</th>
                    {selectedPokemon.map((pokemon) => (
                      <th key={pokemon.id} className="text-center py-4 px-2">
                        <div className="flex flex-col items-center">
                          <img
                            src={pokemon.image}
                            alt={pokemon.displayName}
                            className="w-12 h-12 object-contain mb-2"
                          />
                          <div className="font-semibold text-gray-900">{pokemon.displayName}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(selectedPokemon[0].baseStats).map(([stat, _]) => {
                    const winner = getStatWinner(stat);
                    const statNames = {
                      hp: 'HP',
                      attack: 'Attack',
                      defense: 'Defense',
                      specialAttack: 'Sp. Attack',
                      specialDefense: 'Sp. Defense',
                      speed: 'Speed'
                    };
                    
                    return (
                      <tr key={stat} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-2 font-medium text-gray-900">
                          {statNames[stat]}
                        </td>
                        {selectedPokemon.map((pokemon) => {
                          const isWinner = winner && pokemon.id === winner.id;
                          return (
                            <td key={pokemon.id} className="py-4 px-2 text-center">
                              <div className={`inline-flex items-center justify-center w-16 h-8 rounded-full font-bold ${
                                isWinner 
                                  ? 'bg-green-100 text-green-800 ring-2 ring-green-300' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {pokemon.baseStats[stat]}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  
                  {/* Total Stats */}
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td className="py-4 px-2 font-bold text-gray-900">Total</td>
                    {selectedPokemon.map((pokemon) => {
                      const total = Object.values(pokemon.baseStats).reduce((sum, stat) => sum + stat, 0);
                      const maxTotal = Math.max(...selectedPokemon.map(p => 
                        Object.values(p.baseStats).reduce((sum, stat) => sum + stat, 0)
                      ));
                      const isWinner = total === maxTotal;
                      
                      return (
                        <td key={pokemon.id} className="py-4 px-2 text-center">
                          <div className={`inline-flex items-center justify-center w-20 h-8 rounded-full font-bold ${
                            isWinner 
                              ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-300' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {total}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Detailed Comparison */}
        {selectedPokemon.length > 1 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Abilities Comparison */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Abilities</h3>
              <div className="space-y-4">
                {selectedPokemon.map((pokemon) => (
                  <div key={pokemon.id} className="border-b border-gray-100 pb-4 last:border-b-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <img
                        src={pokemon.image}
                        alt={pokemon.displayName}
                        className="w-8 h-8 object-contain"
                      />
                      <span className="font-semibold text-gray-900">{pokemon.displayName}</span>
                    </div>
                    <div className="ml-11 space-y-1">
                      {pokemon.abilities.map((ability) => (
                        <div key={ability} className="text-sm text-gray-600">
                          {ability}
                        </div>
                      ))}
                      {pokemon.hiddenAbilities.map((ability) => (
                        <div key={ability} className="text-sm text-purple-600">
                          {ability} <span className="text-xs">(Hidden)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Physical Stats */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Physical Stats</h3>
              <div className="space-y-4">
                {selectedPokemon.map((pokemon) => (
                  <div key={pokemon.id} className="border-b border-gray-100 pb-4 last:border-b-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <img
                        src={pokemon.image}
                        alt={pokemon.displayName}
                        className="w-8 h-8 object-contain"
                      />
                      <span className="font-semibold text-gray-900">{pokemon.displayName}</span>
                    </div>
                    <div className="ml-11 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Height:</span>
                        <span className="font-medium ml-2">{pokemon.height}m</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Weight:</span>
                        <span className="font-medium ml-2">{pokemon.weight}kg</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {selectedPokemon.length === 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-6">⚔️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Start Comparing Pokemon</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Add Pokemon to compare their stats, abilities, and characteristics side-by-side.
            </p>
            <button
              onClick={() => setShowSearch(true)}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add Your First Pokemon</span>
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default ComparePage;